import { AuthSession, User } from '@lumora/common';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { initializeAuth, signOut } from '../thunks/authThunks';

export type UserStatus =
  | 'pending_verification'
  | 'onboarding'
  | 'active'
  | 'suspended';

export interface AuthState {
  user: User | null;
  session: AuthSession | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  userStatus: UserStatus | null;
}

const initialState: AuthState = {
  user: null,
  session: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  userStatus: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    setSession: (state, action: PayloadAction<AuthSession | null>) => {
      state.session = action.payload;
      state.user = action.payload?.user || null;
      state.isAuthenticated = !!action.payload;
      state.error = null;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    clearError: state => {
      state.error = null;
    },
    setUserStatus: (state, action: PayloadAction<UserStatus | null>) => {
      state.userStatus = action.payload;
    },
    // Additional reducers for mobile compatibility
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload;
      state.isAuthenticated = true;
      state.error = null;
    },
    clearUser: state => {
      state.user = null;
      state.isAuthenticated = false;
      state.error = null;
      state.userStatus = null;
    },
  },
  extraReducers: builder => {
    builder
      // Initialize auth
      .addCase(initializeAuth.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        state.session = action.payload.session;
        state.user = action.payload.session?.user || null;
        state.isAuthenticated = !!action.payload.session;
        // hasGoals will be checked separately
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.session = null;
        state.user = null;
        state.isAuthenticated = false;
      })
      // Sign out
      .addCase(signOut.pending, state => {
        state.isLoading = true;
      })
      .addCase(signOut.fulfilled, state => {
        state.isLoading = false;
        state.session = null;
        state.user = null;
        state.isAuthenticated = false;
        state.userStatus = null;
        state.error = null;
      })
      .addCase(signOut.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setSession,
  setError,
  clearError,
  setUserStatus,
  setLoading,
  setUser,
  clearUser,
} = authSlice.actions;
export default authSlice.reducer;
